<template>
    <div class="search-page">
        <!-- 引入Header组件 -->
        <mobileHeader v-if="isMobile == 'Mobile'" touterNum=1></mobileHeader>
        <Header v-else></Header>

        <!-- 搜索结果内容 -->
        <div class="search-content" :class="{ Mobileclass: isMobile == 'Mobile' }">
            <!-- 搜索输入框 -->
            <div v-if="isMobile == 'Mobile'" class="search-input-section">
                <div class="search-input-wrapper" @click="focusInput">
                    <div class="search-icon">
                        <img src="@/assets/search.png" alt="search" />
                    </div>
                    <input ref="searchInputRef" v-model="localSearchQuery" type="text" :placeholder="placeholderLabels"
                        class="search-input" @keyup.enter="performSearch" @input="handleInputChange" autocomplete="off"
                        autocorrect="off" autocapitalize="off" spellcheck="false" inputmode="search"
                        enterkeyhint="search" />
                    <button v-if="localSearchQuery" @click="clearInput" class="close-btn">
                        <span>×</span>
                    </button>
                </div>
            </div>

            <!-- 搜索结果头部 -->
            <div v-else class="search-results-header">
                <div v-if="searchQuery" class="search-title">
                    {{ resultLabel }}'{{ searchQuery }}'
                </div>
            </div>

            <!-- 加载状态 -->
            <div v-if="loading" class="loading">搜索中...</div>

            <!-- 错误状态 -->
            <div v-else-if="error" class="error">
                {{ error }}
            </div>

            <!-- 搜索结果 -->
            <div v-else-if="!hasNoResults">
                <div v-for="(item, index) in searchResults" :key="index" class="exhibitions">
                    <div v-if="item?.result_list && item?.result_list.length > 0" class="exhibitions-container">
                        <h3 class="exhibitions_title">
                            {{ item?.result_title }} ({{ item?.result_count }})
                        </h3>

                        <div class="exhibitions-grid" :class="getGridClass(item?.result_title)">
                            <div v-for="exhibition in item?.result_list" :key="exhibition.id" class="exhibition-card"
                                :class="getCardClass(item?.result_title)"
                                @click="isClickableCard(item?.result_title) ? toDetails(exhibition, item?.result_title) : null">
                                <div class="exhibition-image"
                                    @click="isClickableCard(item?.result_title) ? toDetails(exhibition, item?.result_title) : null">
                                    <video v-if="isVideoMedia(item?.result_title)" :src="exhibition.image"
                                        :alt="exhibition.title" controls></video>
                                    <img v-else :src="exhibition.image" :alt="exhibition.title" />
                                </div>
                                <!-- 使用配置化的组件渲染 -->
                                <component :is="'div'" :class="getContentClass(item?.result_title)"
                                    v-html="renderExhibitionContent(exhibition, item?.result_title)">
                                </component>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 无搜索结果 -->
            <div v-else-if="!loading && hasNoResults" class="no-results">
                <p>{{ noResultLabel }}</p>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, ref, nextTick, watch } from "vue";
import { useRouter } from "vue-router";
import { searchState, searchMethods } from "../utils/searchStore";
import { searchLabels } from "../utils/i18nLabels";
import { currentLanguage } from "../utils/lang";
import backIcon from "../assets/back.png";
const isMobile = localStorage.getItem("userAgent");

const router = useRouter();
const searchInputRef = ref(null);
const localSearchQuery = ref("");

// 获取图片URL的函数
function getImageUrl(imageName) {
    switch (imageName) {
        case 'back':
            return backIcon;
        default:
            return '';
    }
}

/**
 * 配置化的展示组件和样式配置
 * 每个配置项说明：
 * - keywords: 匹配的关键词数组（中英文）
 * - gridClass: 网格布局CSS类名
 * - cardClass: 卡片CSS类名
 * - contentClass: 内容区域CSS类名
 * - components: 要显示的组件配置数组
 * - isClickableCard: 整个卡片是否可点击
 * - mediaType: 媒体类型（image/video）
 */
const displayConfig = {
    // 当前展出配置
    current: {
        keywords: ['当前', 'current'],
        gridClass: 'grid-current',
        cardClass: 'card-current',
        contentClass: 'content-current',
        isClickableCard: false,
        mediaType: 'image',
        components: [
            {
                show: true,
                className: 'tips',
                template: (exhibition) => `<p class="tips">${exhibition.tips || "展览"}</p>`
            },
            {
                show: true,
                template: (exhibition) => `<p class="tit app-title">${exhibition.title}</p>`
            },
            {
                show: true,
                template: (exhibition) => `<p class="tim">${exhibition.start_time} - ${exhibition.end_time}</p>`
            },
            {
                show: true,
                template: (exhibition) => exhibition.location ? `<p class="where">${exhibition.location}</p>` : ''
            },
            {
                show: true,
                template: (exhibition) => exhibition.content ? `<p class="cont">${exhibition.content}</p>` : ''
            },
            {
                show: true,
                template: (exhibition, resultTitle) => `
                        <div class="tocon flex" data-exhibition-id="${exhibition.id}" data-result-title="${resultTitle}">
                            <p>详情页面</p>
                            <img src="${getImageUrl('back')}" alt="">
                        </div>
                    `
            }
        ]
    },
    // 经典展览配置
    iconic: {
        keywords: ['经典', 'iconic'],
        gridClass: 'grid-iconic',
        cardClass: 'card-iconic',
        contentClass: 'content-iconic',
        isClickableCard: true,
        mediaType: 'image',
        components: [
            {
                show: true,
                template: (exhibition) => `<p class="tit app-title">${exhibition.title}</p>`
            },
            {
                show: true,
                template: (exhibition) => `<p class="tim">${exhibition.start_time} - ${exhibition.end_time}</p>`
            }
        ]
    },
    // 即将展出配置
    upcoming: {
        keywords: ['即将', 'upcoming'],
        gridClass: 'grid-current',
        cardClass: 'card-current',
        contentClass: 'content-current',
        isClickableCard: false,
        mediaType: 'image',
        routeType: 'exhibition',
        components: [
            {
                show: true,
                template: (exhibition) => `<p class="tips">${exhibition.tips || "展览"}</p>`
            },
            {
                show: true,
                template: (exhibition) => `<p class="tit app-title">${exhibition.title}</p>`
            },
            {
                show: true,
                template: (exhibition) => `<p class="tim">${exhibition.start_time} - ${exhibition.end_time}</p>`
            },
            {
                show: true,
                template: (exhibition) => exhibition.location ? `<p class="where">${exhibition.location}</p>` : ''
            },
            {
                show: true,
                template: (exhibition) => exhibition.content ? `<p class="cont">${exhibition.content}</p>` : ''
            },
            {
                show: true,
                template: (exhibition, resultTitle) => `
                        <div class="tocon flex" data-exhibition-id="${exhibition.id}" data-result-title="${resultTitle}">
                            <p>详情页面</p>
                            <img src="${getImageUrl('back')}" alt="">
                        </div>
                    `
            }
        ]
    },
    // 报道配置
    report: {
        keywords: ['报道', 'report'],
        gridClass: 'grid-video',
        cardClass: 'card-video',
        contentClass: 'content-video',
        isClickableCard: true,
        mediaType: 'image',
        components: [
            {
                show: true,
                template: (exhibition) => `<p class="tit app-title">${exhibition.title}</p>`
            },
            {
                show: true,
                template: (exhibition) => `<p class="tim">${exhibition.created_at}</p>`
            }
        ]
    },
    // 视频配置
    video: {
        keywords: ['视频', 'video'],
        gridClass: 'grid-video',
        cardClass: 'card-video',
        contentClass: 'content-video',
        isClickableCard: true,
        mediaType: 'video',
        components: [
            {
                show: true,
                template: (exhibition) => `<p class="tit app-title">${exhibition.title}</p>`
            },
            {
                show: true,
                template: (exhibition) => `<p class="tim">${exhibition.created_at}</p>`
            }
        ]
    },
    // 图片展览配置
    photes: {
        keywords: ['图片', 'photes'],
        gridClass: 'grid-video',
        cardClass: 'card-video',
        contentClass: 'content-video',
        isClickableCard: true,
        mediaType: 'image',
        components: [
            {
                show: true,
                template: (exhibition) => `<p class="tit app-title">${exhibition.title}</p>`
            },
            {
                show: true,
                template: (exhibition) => `<p class="tim">${exhibition.created_at}</p>`
            }
        ]
    },
    // 公教活动配置
    publiceducation: {
        keywords: ['公教活动', 'public education'],
        gridClass: 'grid-publiceducation',
        cardClass: 'card-publiceducation',
        contentClass: 'content-publiceducation',
        isClickableCard: false,
        mediaType: 'image',
        components: [
            {
                show: true,
                template: (exhibition) => `<p class="tit app-title">${exhibition.title}</p>`
            },
        ]
    },
    // 公益讲解配置
    publicguide: {
        keywords: ['公益讲解', 'public guide'],
        gridClass: 'grid-publicguide',
        cardClass: 'card-publicguide',
        contentClass: 'content-publicguide',
        isClickableCard: true,
        mediaType: 'image',
        components: [
            {
                show: true,
                template: (exhibition) => `<p class="cont">${exhibition.content}</p>`
            },
        ]
    },
    // 艺术品商店配置
    artstore: {
        keywords: ['艺术品商店', 'artshop'],
        gridClass: 'grid-artshop',
        cardClass: 'card-artshop',
        contentClass: 'content-artshop',
        isClickableCard: true,
        mediaType: 'image',
        components: [
            {
                show: true,
                template: (exhibition) => `<p class="tit app-title">${exhibition.title}</p>`
            },
            {
                show: true,
                template: (exhibition) => exhibition.price ? `<p class="price">¥${exhibition.price}</p>` : ''
            },
        ]
    },

};

// 使用全局搜索状态
const searchQuery = computed(() => searchState.searchQuery);
const searchResults = computed(() => searchState.searchResults);
const loading = computed(() => searchState.loading);
const error = computed(() => searchState.error);
const resultLabel = computed(
    () => searchLabels.results[currentLanguage.value || "zh"]
);
const noResultLabel = computed(
    () => searchLabels.noResults[currentLanguage.value || "zh"]
);

const placeholderLabels = computed(() =>
    searchLabels.placeholder[currentLanguage.value || "zh"]
);

// 判断是否没有搜索结果
const hasNoResults = computed(() => {
    if (searchResults.value.length === 0) {
        return true;
    }

    // 检查所有 item 的 result_list 是否都是空数组
    return searchResults.value.every((item) => {
        return !item.result_list || item.result_list.length === 0;
    });
});

// 执行搜索
async function performSearch() {
    const query = localSearchQuery.value.trim();
    if (query) {
        searchMethods.setSearchQuery(query);
        await searchMethods.performSearch(query);
    }
}

// 处理输入变化
function handleInputChange() {
    // 可以在这里添加实时搜索逻辑
}

// 清空输入框
function clearInput() {
    localSearchQuery.value = "";
    searchInputRef.value?.focus();
}

// 聚焦输入框
function focusInput() {
    if (searchInputRef.value) {
        searchInputRef.value.focus();
    }
}

// 根据result_title获取配置
function getDisplayConfig(resultTitle) {
    if (!resultTitle) return displayConfig.current; // 默认配置

    const title = resultTitle.toLowerCase();

    // 遍历配置，找到匹配的关键词
    for (const [configKey, config] of Object.entries(displayConfig)) {
        if (config.keywords.some(keyword => title.includes(keyword.toLowerCase()))) {
            return config;
        }
    }

    return displayConfig.current; // 默认返回current配置
}

// 跳转到详情页
function toDetails(exhibition, result_title) {
    if (result_title == '图片'||result_title == '报道'|| result_title == '视频') {
        router.push(`/mediadetails?id=${exhibition.id}`);
    }
     else {
        // 展览类型跳转到展览详情页
        const pid = exhibition.pid || 1; // 默认为1，表示展览类型
        router.push(`/details?pid=${pid}&id=${exhibition.id}`);
    }
}

// 根据result_title获取grid样式类
function getGridClass(resultTitle) {
    const config = getDisplayConfig(resultTitle);
    return config.gridClass;
}

// 根据result_title获取卡片样式类
function getCardClass(resultTitle) {
    const config = getDisplayConfig(resultTitle);
    return config.cardClass;
}

// 根据result_title获取内容样式类
function getContentClass(resultTitle) {
    const config = getDisplayConfig(resultTitle);
    return config.contentClass;
}

// 是否是可点击卡片
function isClickableCard(resultTitle) {
    const config = getDisplayConfig(resultTitle);
    console.log(config.isClickableCard,'//')
    return config.isClickableCard;
}

// 是否是视频媒体类型
function isVideoMedia(resultTitle) {
    const config = getDisplayConfig(resultTitle);
    return config.mediaType === 'video';
}

// 渲染展览内容
function renderExhibitionContent(exhibition, resultTitle) {
    const config = getDisplayConfig(resultTitle);
    let html = '';

    // 遍历配置中的组件，按顺序渲染
    config.components.forEach(component => {
        if (component.show) {
            const componentHtml = component.template(exhibition, resultTitle);
            if (componentHtml) {
                html += componentHtml;
            }
        }
    });

    return html;
}


// 监听语言变化
watch(currentLanguage, async (newLang) => {
    console.log('语言切换到:', newLang);

    // 更新本地搜索查询为新语言的默认值
    const newDefaultQuery = searchLabels.placeholder[newLang || "zh"];
    localSearchQuery.value = newDefaultQuery;

    // 更新全局搜索状态
    searchMethods.setSearchQuery(newDefaultQuery);

    // 重新执行搜索
    await searchMethods.performSearch(newDefaultQuery);
});

// 组件挂载时执行搜索
onMounted(async () => {
    console.log(searchResults);

    // 初始化本地搜索查询
    localSearchQuery.value =
        searchState.searchQuery ||
        searchLabels.placeholder[currentLanguage.value || "zh"];

    // 聚焦搜索输入框（移动端不自动聚焦，避免键盘弹出影响体验）
    await nextTick();
    if (searchInputRef.value && isMobile !== 'Mobile') {
        searchInputRef.value.focus();
    }

    // 如果全局状态中没有搜索结果，执行默认搜索
    if (!searchState.searchResults.length && !searchState.loading) {
        const defaultQuery =
            searchState.searchQuery ||
            searchLabels.placeholder[currentLanguage.value || "zh"];
        searchMethods.setSearchQuery(defaultQuery);
        searchMethods.performSearch(defaultQuery);
    }

    // 添加事件委托处理详情链接点击
    await nextTick();
    document.addEventListener('click', handleLinkClick);
});

// 处理详情链接点击事件
function handleLinkClick(event) {
    const linkElement = event.target.closest('.tocon[data-exhibition-id]');
    if (linkElement) {
        event.preventDefault();
        const exhibitionId = linkElement.getAttribute('data-exhibition-id');

        // 构造exhibition对象
        const exhibition = { id: exhibitionId };
        toDetails(exhibition);
    }
}
</script>

<style>
/* 全局样式用于v-html生成的内容 */
/* 当前展出&即将展出 */
.content-current {
    .tips {
        font-size: 18px;
        margin: 10px 0;
    }

    .tit {
        font-size: 22px;
        color: #000;
        margin-bottom: 10px;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        /*设置省略号在容器第四行文本后*/
        -webkit-line-clamp: 1;
        overflow: hidden;
    }

    .tim {
        font-size: 16px;
        color: #727272;
        margin-bottom: 8px;
    }

    .where {
        font-size: 16px;
        color: #727272;
        margin-bottom: 8px;
    }

    .cont {
        font-size: 17px;
        color: #000;
        margin-top: 10px;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        /*设置省略号在容器第四行文本后*/
        -webkit-line-clamp: 4;
        overflow: hidden;
        min-height: 96px;
    }

    .tocon {
        justify-content: flex-start;
        padding-top: 90px;
        cursor: pointer;
        display: flex;

        p {
            font-size: 20px;
            color: #000;
        }

        img {
            width: 10px;
            height: 10px;
            transform: rotate(180deg);
            margin-left: 20px;
        }
    }
}

/* 经典展览 */
.content-iconic {
    padding: 20px 16px 16px 16px;

    .tit {
        font-size: 22px;
        color: #000;
        margin-bottom: 16px;
        white-space: nowrap;
        /* 不换行 */
        overflow: hidden;
        /* 隐藏超出的内容 */
        text-overflow: ellipsis;
        /* 用省略号表示被隐藏的部分 */

    }
}

/* 视频 */
.content-video {
    .tit {
        font-size: 20px;
        color: #000;
        margin: 10px 0;
    }

    .tim {
        font-size: 16px;
        color: #3a3a3a;
    }

}

/* 公教活动 */
.content-publiceducation {
    .tit {
        font-size: 25px;
        color: #000;
        margin: 10px 0;
    }
}

/* 公益讲解 */
.content-publicguide {
    .cont {
        font-size: 18px;
        color: #000;
        margin-top: 10px;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 4;
        overflow: hidden;
    }
}

/* 艺术品商店 */
.content-artshop {
    .tit {
        font-size: 18px;
        color: #000;
        margin: 10px 0;
    }

    .price {
        font-size: 18px;
        color: #969696;
    }
}
</style>

<style scoped>
.search-page {
    width: 100%;
    overflow: hidden;

    .tips {
        font-size: 30px;
    }

    .search-content {
         min-height: 400px;
        margin-top: 100px;
        padding-top: 50px;
        padding-bottom: 50px;
        padding-left: 50px;
        padding-right: 50px;
        background-color: #ccc;
        text-align: left;

        /* 搜索输入框样式 - 与header.vue保持一致 */
        .search-input-section {
            margin-bottom: 40px;
            display: flex;
            justify-content: center;
            align-items: center;
        }

        .search-input-wrapper {
            display: flex;
            align-items: center;
            width: 100%;
            max-width: 800px;
            background-color: #f5f5f5;
            border-radius: 15px;
            padding: 12px 20px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
            border: 1px solid #0b90f2;
            cursor: text;
            -webkit-tap-highlight-color: transparent;
        }

        .search-icon {
            margin-right: 12px;
            opacity: 0.6;
            display: flex;
            align-items: center;
        }

        .search-icon img {
            width: 18px;
            height: 18px;
        }

        .search-input {
            flex: 1;
            border: none;
            background: transparent;
            font-size: 18px;
            font-family: "宋体";
            color: #3e3e3e;
            outline: none;
            padding: 0;
            height: 24px;
            line-height: 24px;
        }

        .search-input::placeholder {
            color: #999;
            font-size: 18px;
        }

        .close-btn {
            background: none;
            border: none;
            cursor: pointer;
            padding: 0;
            margin-left: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 24px;
            height: 24px;
            border-radius: 50%;
            transition: background-color 0.2s;
        }

        .close-btn:hover {
            background-color: rgba(0, 0, 0, 0.1);
        }

        .close-btn span {
            font-size: 20px;
            color: #666;
            line-height: 1;
            font-weight: normal;
        }

        .search-results-header {
            margin-bottom: 50px;
            text-align: left;

            .search-title {
                font-size: 39px;
                color: #232323;
                text-align: left;
            }
        }

        .loading,
        .error {
            text-align: center;
            padding: 60px 20px;
            font-size: 18px;
            color: #666;
        }

        .error {
            color: #e74c3c;
        }

        .exhibitions-container {
            padding-bottom: 30px;
            border-bottom: 2px solid #a2a0a0;
        }

        .exhibitions-container {
            .exhibitions_title {
                text-align: left;
                font-size: 24px;
                color: #000;
                margin-bottom: 50px;
                margin-top: 50px;
            }

            .exhibitions-grid {
                display: flex;
                flex-wrap: wrap;
                gap: 30px;

                /* 不同布局样式 */
                &.grid-iconic {
                    .exhibition-card {
                        width: 24%;
                    }
                }

                &.grid-publicguide {
                    .exhibition-card {
                        width: 32%;

                        .exhibition-image {
                            height: 240px;
                        }
                    }
                }

                &.grid-video {
                    .exhibition-card {
                        width: 24%;

                        .exhibition-image {
                            height: 200px;
                        }
                    }
                }

                &.grid-artshop {
                    gap: 20px;
                    .exhibition-card {
                        width: 24%;

                        .exhibition-image {
                            height: 200px;
                        }
                    }
                }

                .exhibition-card {
                    overflow: hidden;
                    transition: all 0.3s ease;
                    cursor: pointer;
                    width: 30%;

                    /* 经典展览样式 */
                    &.card-iconic {
                        background: #f5f5f5;
                        margin-bottom: 20px;
                    }

                    /* 当前/即将展览样式 */
                    &.card-current {
                        padding-bottom: 20px;
                        font-size: 16px;
                        color: #727272;
                        text-align: left;
                        cursor: text;
                        .exhibition-image {
                            cursor: text;
                         }
                    }

                    /* 样式一致 */
                    &.card-video,
                    &.card-artshop,
                    &.card-publicguide,
                    &.card-publiceducation {
                        margin-bottom: 30px;
                    }

                    .exhibition-image {
                        width: 100%;
                        height: 288px;
                        overflow: hidden;
                        cursor: pointer;
                    }

                    .exhibition-image img,
                    .exhibition-image video {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                        transition: transform 0.3s ease;
                        display: block;
                    }

                }

                .exhibition-card:hover {
                    /* transform: translateY(-5px); */
                    /* box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15); */
                }

                .exhibition-card:hover .exhibition-image img {
                    transform: scale(1.05);
                }
            }

            .no-results {
                text-align: center;
                padding: 60px 20px;
                font-size: 18px;
                color: #666;
            }
        }
    }

    /* 移动端适配 - 使用条件类名 */
    .Mobileclass {
        min-height: 200px;
        margin-top: 80px;
        padding: 20px 15px;
        text-align: left;

        .search-input-section {
            padding: 0;
            margin-bottom: 30px;
        }

        .search-input-wrapper {
            padding: 10px 16px;
            border-radius: 15px;
            cursor: text;
            -webkit-tap-highlight-color: transparent;
        }

        .search-icon img {
            width: 16px;
            height: 16px;
        }

        .search-input {
            font-size: 14px;
        }

        .search-input::placeholder {
            font-size: 14px;
        }

        .close-btn {
            width: 20px;
            height: 20px;
        }

        .close-btn span {
            font-size: 18px;
        }

        .search-title {
            font-size: 24px;
            text-align: left;
        }

        .search-results-header {
            text-align: left;
        }

        .exhibitions-container {
            padding-bottom: 20px;
            border-bottom: 2px solid #a2a0a0;
            margin-bottom: 20px;
        }

        .exhibitions-container {
            .exhibitions_title {
                font-size: 20px;
                margin-bottom: 30px;
                margin-top: 30px;
            }

            .exhibitions-grid {
                display: block;

                /* 移动端所有布局都变为单列 */
                &.grid-current,
                &.grid-iconic,
                &.grid-video,
                &.grid-artshop,
                &.grid-publicguide,
                &.grid-publiceducation {
                    .exhibition-card {
                        width: 100%;
                        margin-bottom: 30px;

                        &:first-child {
                            padding-top: 0;
                        }
                    }
                }

                .exhibition-card {
                    width: 100%;
                    margin-bottom: 30px;

                    &:first-child {
                        padding-top: 0;
                    }

                    .exhibition-image {
                        height: 200px;
                    }
                }
            }
        }
    }
}
</style>